import React, { useEffect, useState, useCallback, useRef, useContext } from "react";
import { PermissionsContext } from "../../utils/PermissionsContext";
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Alert, Tooltip, Divider, Stepper, Step, StepLabel,
  InputAdornment, TablePagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ViewListIcon from "@mui/icons-material/ViewList";
import DownloadIcon from "@mui/icons-material/Download";
import PaymentIcon from "@mui/icons-material/Payment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import OwnerInfoPopover from "./OwnerInfoPopover";
import api from "../../utils/api";

/* ── Constants ──────────────────────────────────────────────────────────── */
const RES_STATUS_STYLES = {
  confirmed:   { bg: "rgba(47,99,173,0.12)",  color: "#1d4ed8",  label: "Confirmed" },
  pending:     { bg: "rgba(217,119,6,0.12)",  color: "#b45309",  label: "Pending" },
  checked_in:  { bg: "rgba(22,163,74,0.12)",  color: "#15803d",  label: "Checked In" },
  checked_out: { bg: "rgba(107,119,140,0.1)", color: "#374151",  label: "Checked Out" },
  cancelled:   { bg: "rgba(220,38,38,0.1)",   color: "#dc2626",  label: "Cancelled" },
  no_show:     { bg: "rgba(127,29,29,0.1)",   color: "#7f1d1d",  label: "No Show" },
};

const RES_TYPES = ["transient", "monthly", "seasonal", "annual"];
const POWER_OPTIONS = ["none", "30amp", "50amp", "100amp"];
const CHARGE_TYPES = [
  "slip_rental", "fuel", "electric", "water", "pump_out",
  "storage", "amenity", "fee", "supply", "tax", "discount", "other",
];
const BLANK_CHARGE = {
  service_item_id: "", charge_type: "fee", description: "",
  quantity: "1", unit_rate: "", notes: "",
};

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const fmtDate = (d) => { if (!d) return "N/A"; const dt = new Date(String(d).slice(0, 10) + "T00:00:00"); return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };
const fmtDateTime = (iso) => { if (!iso) return "N/A"; return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); };

/* ── Calendar helpers ───────────────────────────────────────────────────── */
const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const addDaysToDate = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfWeek = (d) => {
  const dow = d.getDay(); // 0=Sun
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - (dow === 0 ? 6 : dow - 1));
};
const monthGrid = (d) => {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const dow = first.getDay();
  const weekStart = new Date(d.getFullYear(), d.getMonth(), 1 - (dow === 0 ? 6 : dow - 1));
  return Array.from({ length: 42 }, (_, i) => addDaysToDate(weekStart, i));
};
const resActiveOnDay = (r, ds) =>
  String(r.start_date).slice(0, 10) <= ds && String(r.end_date).slice(0, 10) >= ds;

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/* ── Shared styles ──────────────────────────────────────────────────────── */
const primaryBtnSx = {
  backgroundColor: "#2f63ad", color: "#fff", fontWeight: 700, borderRadius: "10px",
  textTransform: "none", boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};
const secondaryBtnSx = { borderRadius: "10px", textTransform: "none", fontWeight: 600 };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" } };
const dialogPaperSx = { borderRadius: "18px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" };

/* ── Status chip ────────────────────────────────────────────────────────── */
const ResStatusChip = ({ status }) => {
  const s = RES_STATUS_STYLES[status] || RES_STATUS_STYLES.pending;
  return <Chip label={s.label} size="small" sx={{ fontWeight: 700, fontSize: "0.72rem", height: 22, borderRadius: "6px", backgroundColor: s.bg, color: s.color }} />;
};

/* ══════════════════════════════════════════════════════════════════════════
   EDIT RESERVATION DIALOG
══════════════════════════════════════════════════════════════════════════ */
const EditReservationDialog = ({ open, reservation, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open && reservation) {
      setForm({
        start_date:       String(reservation.start_date || "").slice(0, 10),
        end_date:         String(reservation.end_date   || "").slice(0, 10),
        reservation_type: reservation.reservation_type  || "transient",
        status:           reservation.status            || "confirmed",
        notes:            reservation.notes             || "",
      });
      setErr("");
    }
  }, [open, reservation]);

  const handleSave = async () => {
    if (!form.start_date || !form.end_date) { setErr("Arrival and departure dates are required."); return; }
    if (form.start_date >= form.end_date)   { setErr("Departure must be after arrival."); return; }
    setSaving(true); setErr("");
    try {
      await api.put(`/slip-reservations/${reservation.id}`, {
        start_date:       form.start_date,
        end_date:         form.end_date,
        reservation_type: form.reservation_type,
        status:           form.status,
        notes:            form.notes || null,
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || "Failed to save changes.");
    } finally { setSaving(false); }
  };

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const nights = form.start_date && form.end_date
    ? Math.max(0, (new Date(form.end_date) - new Date(form.start_date)) / 86400000)
    : 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ fontWeight: 700, pt: 3 }}>
        Edit Reservation
        {reservation && (
          <Typography variant="body2" sx={{ color: "#6b778c", fontWeight: 400, mt: 0.25 }}>
            {reservation.vessel_name} · Slip {reservation.slip_number}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="date" label="Arrival *"
              value={form.start_date || ""} onChange={setField("start_date")}
              InputLabelProps={{ shrink: true }} sx={fieldSx} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="date" label="Departure *"
              value={form.end_date || ""} onChange={setField("end_date")}
              InputLabelProps={{ shrink: true }} sx={fieldSx} />
          </Grid>
          {nights > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: "#6b778c" }}>
                {nights} night{nights !== 1 ? "s" : ""}
              </Typography>
            </Grid>
          )}
          <Grid item xs={6}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Reservation Type</InputLabel>
              <Select value={form.reservation_type || "transient"} label="Reservation Type" onChange={setField("reservation_type")}>
                {RES_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Status</InputLabel>
              <Select value={form.status || "confirmed"} label="Status" onChange={setField("status")}>
                {Object.entries(RES_STATUS_STYLES).map(([v, { label }]) => (
                  <MenuItem key={v} value={v}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline rows={3} label="Notes"
              value={form.notes || ""} onChange={setField("notes")} sx={fieldSx} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={primaryBtnSx} disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   CALENDAR VIEW
══════════════════════════════════════════════════════════════════════════ */
const CalendarView = ({ reservations, calView, calDate, onNavigate, onViewChange, onResClick }) => {
  const navigate = (delta) => {
    const d = new Date(calDate);
    if (calView === "day")   d.setDate(d.getDate() + delta);
    if (calView === "week")  d.setDate(d.getDate() + delta * 7);
    if (calView === "month") d.setMonth(d.getMonth() + delta);
    onNavigate(d);
  };

  const periodLabel = () => {
    if (calView === "month")
      return calDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    if (calView === "week") {
      const mon = startOfWeek(calDate);
      const sun = addDaysToDate(mon, 6);
      return mon.getMonth() === sun.getMonth()
        ? `${mon.toLocaleDateString("en-US", { month: "short", day: "numeric" })}. ${sun.toLocaleDateString("en-US", { day: "numeric", year: "numeric" })}`
        : `${mon.toLocaleDateString("en-US", { month: "short", day: "numeric" })}. ${sun.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return calDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  };

  const todayStr = today();

  /* ── Month view ── */
  const renderMonth = () => {
    const days = monthGrid(calDate);
    const curMonth = calDate.getMonth();
    return (
      <Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
          {WEEK_DAYS.map((d) => (
            <Box key={d} sx={{ textAlign: "center", py: 0.75 }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#6b778c", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {d}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "3px" }}>
          {days.map((day) => {
            const ds = isoDate(day);
            const isToday      = ds === todayStr;
            const isThisMonth  = day.getMonth() === curMonth;
            const dayRes       = reservations.filter((r) => resActiveOnDay(r, ds));
            const visible      = dayRes.slice(0, 3);
            const overflow     = dayRes.length - 3;
            return (
              <Box key={ds} sx={{
                minHeight: 96, p: "5px 5px 4px",
                backgroundColor: isToday ? "rgba(47,99,173,0.04)" : "#fff",
                border: `1px solid ${isToday ? "rgba(47,99,173,0.25)" : "rgba(0,0,0,0.07)"}`,
                borderRadius: "8px",
                opacity: isThisMonth ? 1 : 0.4,
              }}>
                <Box sx={{
                  width: 22, height: 22, borderRadius: "50%",
                  backgroundColor: isToday ? "#2f63ad" : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center", mb: 0.4,
                }}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: isToday ? 700 : 500, color: isToday ? "#fff" : isThisMonth ? "#223142" : "#9aa5b4" }}>
                    {day.getDate()}
                  </Typography>
                </Box>
                {visible.map((r) => {
                  const st = RES_STATUS_STYLES[r.status] || RES_STATUS_STYLES.pending;
                  return (
                    <Box key={r.id} onClick={() => onResClick(r)}
                      sx={{ mb: "2px", px: "5px", py: "1px", borderRadius: "4px", backgroundColor: st.bg, cursor: "pointer", "&:hover": { opacity: 0.75 } }}>
                      <Typography sx={{ fontSize: "0.66rem", fontWeight: 600, color: st.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.vessel_name}
                      </Typography>
                    </Box>
                  );
                })}
                {overflow > 0 && (
                  <Typography sx={{ fontSize: "0.62rem", color: "#9aa5b4", pl: "3px" }}>+{overflow} more</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  /* ── Week view ── */
  const renderWeek = () => {
    const mon = startOfWeek(calDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDaysToDate(mon, i));
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "6px" }}>
        {weekDays.map((day, idx) => {
          const ds = isoDate(day);
          const isToday  = ds === todayStr;
          const dayRes   = reservations.filter((r) => resActiveOnDay(r, ds));
          return (
            <Box key={ds}>
              <Box sx={{
                textAlign: "center", py: 1, mb: 0.75,
                backgroundColor: isToday ? "rgba(47,99,173,0.07)" : "#f8fafc",
                borderRadius: "10px",
                border: `1px solid ${isToday ? "rgba(47,99,173,0.25)" : "rgba(0,0,0,0.06)"}`,
              }}>
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#6b778c", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {WEEK_DAYS[idx]}
                </Typography>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: isToday ? 800 : 500, color: isToday ? "#2f63ad" : "#223142", lineHeight: 1.2 }}>
                  {day.getDate()}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minHeight: 60 }}>
                {dayRes.length === 0
                  ? <Box sx={{ height: 36, borderRadius: "6px", border: "1px dashed rgba(0,0,0,0.07)" }} />
                  : dayRes.map((r) => {
                      const st = RES_STATUS_STYLES[r.status] || RES_STATUS_STYLES.pending;
                      return (
                        <Box key={r.id} onClick={() => onResClick(r)}
                          sx={{ p: "5px 7px", borderRadius: "7px", backgroundColor: st.bg, cursor: "pointer", border: `1px solid ${st.color}28`, "&:hover": { opacity: 0.8 } }}>
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: st.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2 }}>
                            {r.vessel_name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.62rem", color: "#6b778c", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {r.slip_number}
                          </Typography>
                        </Box>
                      );
                    })
                }
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  /* ── Day view ── */
  const renderDay = () => {
    const ds = isoDate(calDate);
    const dayRes = reservations.filter((r) => resActiveOnDay(r, ds));
    if (dayRes.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <DirectionsBoatIcon sx={{ fontSize: 48, color: "#d0d7e0", mb: 1, display: "block", mx: "auto" }} />
          <Typography variant="body2" sx={{ color: "#9aa5b4" }}>No reservations on this day.</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {dayRes.map((r) => {
          const st = RES_STATUS_STYLES[r.status] || RES_STATUS_STYLES.pending;
          return (
            <Paper key={r.id} elevation={0} onClick={() => onResClick(r)}
              sx={{ p: 2, borderRadius: "12px", cursor: "pointer", border: `1px solid ${st.color}30`, backgroundColor: st.bg, "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.09)" } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.75 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142" }}>{r.vessel_name}</Typography>
                  <Typography variant="caption" sx={{ color: "#6b778c" }}>
                    Slip {r.slip_number} · {r.dock_name}
                  </Typography>
                </Box>
                <ResStatusChip status={r.status} />
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <Typography variant="caption" sx={{ color: "#6b778c" }}>
                  {fmtDate(r.start_date)} → {fmtDate(r.end_date)}
                </Typography>
                <Typography variant="caption" sx={{ color: "#6b778c" }}>
                  Owner: <strong style={{ color: "#223142" }}>{r.owner_name || "N/A"}</strong>
                </Typography>
                <Typography variant="caption" sx={{ color: "#6b778c" }}>
                  Type: <strong style={{ color: "#223142" }}>{r.reservation_type}</strong>
                </Typography>
                {r.total_amount != null && (
                  <Typography variant="caption" sx={{ color: "#6b778c" }}>
                    Total: <strong style={{ color: "#2f63ad" }}>${Number(r.total_amount).toFixed(2)}</strong>
                  </Typography>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      {/* Navigation bar */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <IconButton size="small" onClick={() => navigate(-1)}
            sx={{ border: "1px solid rgba(0,0,0,0.1)", borderRadius: "8px", width: 32, height: 32 }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", minWidth: 200, textAlign: "center", fontSize: "0.95rem" }}>
            {periodLabel()}
          </Typography>
          <IconButton size="small" onClick={() => navigate(1)}
            sx={{ border: "1px solid rgba(0,0,0,0.1)", borderRadius: "8px", width: 32, height: 32 }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Button size="small" startIcon={<TodayIcon sx={{ fontSize: 16 }} />}
            onClick={() => onNavigate(new Date())}
            sx={{ ml: 0.5, borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.8rem",
              border: "1px solid rgba(47,99,173,0.25)", color: "#2f63ad", py: "3px", px: 1.25 }}>
            Today
          </Button>
        </Box>

        {/* Day / Week / Month toggle */}
        <Box sx={{ display: "flex", gap: "2px", backgroundColor: "#f1f5f9", borderRadius: "10px", p: "3px" }}>
          {[["day", "Day"], ["week", "Week"], ["month", "Month"]].map(([v, label]) => (
            <Button key={v} size="small" onClick={() => onViewChange(v)}
              sx={{
                borderRadius: "8px", textTransform: "none", fontWeight: 600,
                px: 1.5, py: 0.4, minWidth: 58, fontSize: "0.8rem",
                backgroundColor: calView === v ? "#fff" : "transparent",
                color: calView === v ? "#2f63ad" : "#6b778c",
                boxShadow: calView === v ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                "&:hover": { backgroundColor: calView === v ? "#fff" : "rgba(255,255,255,0.6)" },
              }}>
              {label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Calendar grid */}
      <Box sx={{ overflowX: "auto" }}>
        {calView === "month" && renderMonth()}
        {calView === "week"  && renderWeek()}
        {calView === "day"   && renderDay()}
      </Box>
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   RESERVATION CHARGES DIALOG
══════════════════════════════════════════════════════════════════════════ */
const ReservationChargesDialog = ({ open, reservation, marinaId, onClose, onUpdated }) => {
  const [charges, setCharges] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(BLANK_CHARGE);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [serviceItems, setServiceItems] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const itemDebounce = useRef(null);

  const fetchCharges = useCallback(async () => {
    if (!reservation) return;
    setLoading(true);
    try {
      const r = await api.get(`/slip-reservations/${reservation.id}/charges`);
      setCharges(r.data.charges || []);
      setSummary(r.data.summary || null);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [reservation]);

  useEffect(() => {
    if (open && reservation) fetchCharges();
  }, [open, reservation, fetchCharges]);

  useEffect(() => {
    if (!itemSearch.trim()) { setServiceItems([]); return; }
    clearTimeout(itemDebounce.current);
    itemDebounce.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ marina_id: marinaId, search: itemSearch });
        const r = await api.get(`/marina-service-items?${params}`);
        setServiceItems(r.data || []);
      } catch { /* silent */ }
    }, 350);
    return () => clearTimeout(itemDebounce.current);
  }, [itemSearch, marinaId]);

  const openAdd = () => {
    setEditTarget(null); setForm(BLANK_CHARGE); setErr("");
    setItemSearch(""); setServiceItems([]); setAddOpen(true);
  };

  const openEdit = (charge) => {
    setEditTarget(charge);
    setForm({
      service_item_id: charge.service_item_id || "",
      charge_type: charge.charge_type || "fee",
      description: charge.description || "",
      quantity: String(charge.quantity ?? "1"),
      unit_rate: String(charge.unit_rate ?? ""),
      
      notes: charge.notes || "",
    });
    setErr(""); setItemSearch(""); setServiceItems([]); setAddOpen(true);
  };

  const handleSave = async () => {
    if (!form.description.trim()) { setErr("Description is required."); return; }
    if (!form.unit_rate) { setErr("Unit price is required."); return; }
    setSaving(true); setErr("");
    try {
      const payload = {
        charge_type: form.charge_type,
        description: form.description,
        quantity: parseFloat(form.quantity) || 1,
        unit_rate: parseFloat(form.unit_rate),
        
        notes: form.notes || null,
        ...(form.service_item_id ? { service_item_id: form.service_item_id } : {}),
      };
      if (editTarget) {
        await api.put(`/slip-reservations/${reservation.id}/charges/${editTarget.id}`, payload);
      } else {
        await api.post(`/slip-reservations/${reservation.id}/charges`, payload);
      }
      setAddOpen(false);
      fetchCharges();
      onUpdated?.();
    } catch (e) {
      setErr(e.response?.data?.message || "Failed to save charge.");
    } finally { setSaving(false); }
  };

  const handleDelete = async (charge) => {
    setDeleting(charge.id);
    try {
      await api.delete(`/slip-reservations/${reservation.id}/charges/${charge.id}`);
      fetchCharges();
      onUpdated?.();
    } catch { /* silent */ }
    finally { setDeleting(null); }
  };

  const selectServiceItem = (item) => {
    setForm((p) => ({
      ...p,
      service_item_id: item.id,
      charge_type: item.category || p.charge_type,
      description: item.name,
      unit_rate: String(item.unit_rate ?? ""),
      
    }));
    setItemSearch(item.name);
    setServiceItems([]);
  };

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const fmtAmt = (v) => (v != null ? `$${Number(v).toFixed(2)}` : "N/A");

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontWeight: 700, pt: 3 }}>
          Reservation Charges
          {reservation && (
            <Typography variant="body2" sx={{ color: "#6b778c", fontWeight: 400, mt: 0.25 }}>
              {reservation.vessel_name} · Slip {reservation.slip_number} · {fmtDate(reservation.start_date)} → {fmtDate(reservation.end_date)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid rgba(47,99,173,0.1)", overflow: "hidden", mb: 2 }}>
                <TableContainer sx={{ maxHeight: 340 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        {["Type", "Description", "Qty", "Unit Price", "Tax", "Amount", ""].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", backgroundColor: "#f8fafc", whiteSpace: "nowrap" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {charges.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                            <ReceiptLongIcon sx={{ fontSize: 36, color: "#d0d7e0", mb: 1, display: "block", mx: "auto" }} />
                            <Typography variant="body2" sx={{ color: "#9aa5b4" }}>No charges yet.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : charges.map((c) => (
                        <TableRow key={c.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                          <TableCell>
                            <Chip label={c.charge_type?.replace(/_/g, " ")} size="small"
                              sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600, textTransform: "capitalize",
                                backgroundColor: "rgba(47,99,173,0.08)", color: "#2f63ad" }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.82rem", color: "#223142" }}>{c.description}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", color: "#6b778c" }}>{c.quantity}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", fontFamily: "monospace" }}>{fmtAmt(c.unit_rate)}</TableCell>
                          <TableCell sx={{ fontSize: "0.78rem", color: "#6b778c" }}>{"N/A"}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontFamily: "monospace", color: c.charge_type === "discount" ? "#dc2626" : "#223142" }}>
                            {c.charge_type === "discount" ? `-${fmtAmt(c.total_amount)}` : fmtAmt(c.total_amount)}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            <IconButton size="small" sx={{ color: "#2f63ad" }} onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" disabled={deleting === c.id} onClick={() => handleDelete(c)}>
                              {deleting === c.id ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {summary && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", border: "1px solid rgba(47,99,173,0.1)", minWidth: 240 }}>
                    {[
                      ["Base Rental", summary.base_rental],
                      ["Extra Charges", summary.charges_total],
                    ].map(([label, val]) => (
                      <Box key={label} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: "#6b778c" }}>{label}</Typography>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", color: "#445" }}>
                          ${Number(val || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Grand Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, fontFamily: "monospace", color: "#2f63ad" }}>
                        ${Number(summary.grand_total || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              )}

              <Button variant="contained" startIcon={<AddIcon />} sx={primaryBtnSx} onClick={openAdd}>
                Add Charge
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Add / Edit charge sub-dialog ── */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontWeight: 700, pt: 3 }}>{editTarget ? "Edit Charge" : "Add Charge"}</DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          {err && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Search service catalog (optional)" placeholder="Type to search items…"
                value={itemSearch} onChange={(e) => setItemSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }}
                sx={fieldSx} />
              {serviceItems.length > 0 && (
                <Paper elevation={3} sx={{ mt: 0.5, borderRadius: "10px", overflow: "hidden", maxHeight: 180, overflowY: "auto" }}>
                  {serviceItems.map((item) => (
                    <Box key={item.id} onClick={() => selectServiceItem(item)}
                      sx={{ px: 2, py: 1, cursor: "pointer", "&:hover": { backgroundColor: "rgba(47,99,173,0.06)" }, borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                        <Typography variant="caption" sx={{ color: "#6b778c", textTransform: "capitalize" }}>
                          {item.category?.replace(/_/g, " ")} · per {item.unit}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, color: "#2f63ad" }}>
                        ${Number(item.unit_rate).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              )}
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Charge Type *</InputLabel>
                <Select value={form.charge_type} label="Charge Type *" onChange={setField("charge_type")}>
                  {CHARGE_TYPES.map((t) => (
                    <MenuItem key={t} value={t} sx={{ textTransform: "capitalize" }}>{t.replace(/_/g, " ")}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Description *" value={form.description} onChange={setField("description")} sx={fieldSx} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Quantity" type="number" value={form.quantity} onChange={setField("quantity")}
                inputProps={{ step: 0.01, min: 0 }} sx={fieldSx} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Unit Rate ($) *" type="number" value={form.unit_rate} onChange={setField("unit_rate")}
                inputProps={{ step: 0.01, min: 0 }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={fieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={form.notes} onChange={setField("notes")} sx={fieldSx} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setAddOpen(false)} variant="outlined" sx={secondaryBtnSx} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={primaryBtnSx} disabled={saving}>
            {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editTarget ? "Update" : "Add Charge"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   NEW RESERVATION WIZARD
══════════════════════════════════════════════════════════════════════════ */
const NewReservationDialog = ({ open, marinaId, onClose, onCreated }) => {
  const [step, setStep] = useState(0);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableSlips, setAvailableSlips] = useState([]);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [vessels, setVessels] = useState([]);
  const [vesselSearch, setVesselSearch] = useState("");
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [dimensionError, setDimensionError] = useState(null);
  const [conflictError, setConflictError] = useState(null);
  const [err, setErr] = useState("");

  const [searchForm, setSearchForm] = useState({
    start_date: today(), end_date: plusDays(3),
    min_length: "", max_beam: "", max_draft: "", power_required: "",
  });
  const [resForm, setResForm] = useState({ reservation_type: "transient", notes: "" });

  const vesselDebounce = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep(0); setAvailableSlips([]); setSelectedSlip(null);
      setSelectedVessel(null); setVesselSearch(""); setVessels([]);
      setDimensionError(null); setConflictError(null); setErr("");
      setSearchForm({ start_date: today(), end_date: plusDays(3), min_length: "", max_beam: "", max_draft: "", power_required: "" });
      setResForm({ reservation_type: "transient", notes: "" });
    }
  }, [open]);

  const handleAvailabilitySearch = async () => {
    if (!searchForm.start_date || !searchForm.end_date) { setErr("Start and end date are required."); return; }
    setSearching(true); setErr("");
    try {
      const params = new URLSearchParams({ marina_id: marinaId, start_date: searchForm.start_date, end_date: searchForm.end_date });
      if (searchForm.min_length) params.set("min_length", searchForm.min_length);
      if (searchForm.max_beam)   params.set("max_beam",   searchForm.max_beam);
      if (searchForm.max_draft)  params.set("max_draft",  searchForm.max_draft);
      if (searchForm.power_required) params.set("power_required", searchForm.power_required);
      const res = await api.get(`/marina-slips/availability?${params}`);
      setAvailableSlips(res.data || []);
      if ((res.data || []).length === 0) setErr("No available slips match your criteria.");
    } catch { setErr("Availability search failed."); }
    finally { setSearching(false); }
  };

  const selectSlip = (slip) => { setSelectedSlip(slip); setStep(1); setErr(""); };

  // When a vessel is selected in Step 1, auto-populate its dimensions into the
  // search form and re-run the availability search so slips that don't fit are removed.
  const applyVesselDimensions = async (vessel) => {
    const updatedSearch = {
      ...searchForm,
      min_length:    vessel.loa   ? String(vessel.loa)   : searchForm.min_length,
      max_beam:      vessel.beam  ? String(vessel.beam)  : searchForm.max_beam,
      max_draft:     vessel.draft ? String(vessel.draft) : searchForm.max_draft,
      power_required: vessel.power_required && vessel.power_required !== "none"
        ? vessel.power_required
        : searchForm.power_required,
    };
    setSearchForm(updatedSearch);

    // Re-search with vessel dimensions
    if (!updatedSearch.start_date || !updatedSearch.end_date) return;
    setSearching(true);
    try {
      const params = new URLSearchParams({ marina_id: marinaId, start_date: updatedSearch.start_date, end_date: updatedSearch.end_date });
      if (updatedSearch.min_length)    params.set("min_length",    updatedSearch.min_length);
      if (updatedSearch.max_beam)      params.set("max_beam",      updatedSearch.max_beam);
      if (updatedSearch.max_draft)     params.set("max_draft",     updatedSearch.max_draft);
      if (updatedSearch.power_required) params.set("power_required", updatedSearch.power_required);
      const res = await api.get(`/marina-slips/availability?${params}`);
      const newSlips = res.data || [];
      setAvailableSlips(newSlips);
      // If the currently selected slip no longer fits, clear it and warn
      if (selectedSlip && !newSlips.find(s => s.id === selectedSlip.id)) {
        setSelectedSlip(null);
        setErr(`The previously selected slip does not fit ${vessel.vessel_name}. Please choose a compatible slip.`);
        setStep(0);
      }
    } catch { /* silent. dimensions were still applied */ }
    finally { setSearching(false); }
  };

  useEffect(() => {
    if (!vesselSearch.trim()) { setVessels([]); return; }
    clearTimeout(vesselDebounce.current);
    vesselDebounce.current = setTimeout(async () => {
      try {
        const res = await api.get(`/vessels?search=${encodeURIComponent(vesselSearch)}`);
        setVessels(res.data || []);
      } catch { /* silent */ }
    }, 350);
    return () => clearTimeout(vesselDebounce.current);
  }, [vesselSearch]);

  const handleConfirm = async (skipDimension = false) => {
    if (!selectedSlip || !selectedVessel) { setErr("Slip and vessel are required."); return; }
    setSaving(true); setErr(""); setDimensionError(null); setConflictError(null);
    try {
      const payload = {
        slip_id: selectedSlip.id,
        vessel_id: selectedVessel.id,
        start_date: searchForm.start_date,
        end_date: searchForm.end_date,
        reservation_type: resForm.reservation_type,
        notes: resForm.notes,
        ...(skipDimension ? { skip_dimension_check: true } : {}),
      };
      await api.post("/slip-reservations", payload);
      onCreated();
      onClose();
    } catch (e) {
      const data = e.response?.data;
      if (e.response?.status === 422 && (data?.insurance_expired || data?.registration_expired)) {
        setErr(data.message);  // "Insurance expired…" / "Registration expired…". hard block, no override
      } else if (e.response?.status === 422 && data?.dimension_error) {
        setDimensionError(data.message || "Vessel doesn't fit this slip.");
      } else if (e.response?.status === 409 && data?.conflict) {
        setConflictError(data.conflict);
        setErr(data.message || "Slip is already booked for that date range.");
      } else {
        setErr(data?.message || "Failed to create reservation.");
      }
    }
    finally { setSaving(false); }
  };

  const nights = searchForm.start_date && searchForm.end_date
    ? Math.max(0, (new Date(searchForm.end_date) - new Date(searchForm.start_date)) / 86400000)
    : 0;
  const estimatedTotal = selectedSlip?.daily_rate ? (selectedSlip.daily_rate * nights).toFixed(2) : null;

  // Auto-search slips when dates change AND a vessel is already selected
  const searchSlipsForVessel = async (vessel, dates) => {
    const sd = dates?.start_date ?? searchForm.start_date;
    const ed = dates?.end_date   ?? searchForm.end_date;
    if (!sd || !ed || !vessel) return;
    setSearching(true); setErr("");
    try {
      const params = new URLSearchParams({ marina_id: marinaId, start_date: sd, end_date: ed });
      if (vessel.loa)   params.set("min_length", vessel.loa);
      if (vessel.beam)  params.set("max_beam",   vessel.beam);
      if (vessel.draft) params.set("max_draft",  vessel.draft);
      if (vessel.power_required && vessel.power_required !== "none") params.set("power_required", vessel.power_required);
      const res = await api.get(`/marina-slips/availability?${params}`);
      setAvailableSlips(res.data || []);
      if ((res.data || []).length === 0) setErr("No available slips fit this vessel for the selected dates.");
      // Clear selected slip if it no longer fits
      if (selectedSlip && !(res.data || []).find(s => s.id === selectedSlip.id)) setSelectedSlip(null);
    } catch { setErr("Availability search failed."); }
    finally { setSearching(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ fontWeight: 700, pt: 3, pb: 0 }}>New Reservation</DialogTitle>

      <Box sx={{ px: 3, pt: 2, pb: 0 }}>
        <Stepper activeStep={step} alternativeLabel>
          {["Vessel & Slip", "Confirm"].map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2 }}>
        {err && !dimensionError && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}

        {/* ── STEP 0: Select dates + vessel → available slips appear ── */}
        {step === 0 && (
          <Box>
            {/* Dates row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="date" label="Arrival *" value={searchForm.start_date}
                  InputLabelProps={{ shrink: true }} sx={fieldSx}
                  onChange={(e) => {
                    const newDates = { ...searchForm, start_date: e.target.value };
                    setSearchForm(newDates);
                    if (selectedVessel) searchSlipsForVessel(selectedVessel, newDates);
                  }} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="date" label="Departure *" value={searchForm.end_date}
                  InputLabelProps={{ shrink: true }} sx={fieldSx}
                  onChange={(e) => {
                    const newDates = { ...searchForm, end_date: e.target.value };
                    setSearchForm(newDates);
                    if (selectedVessel) searchSlipsForVessel(selectedVessel, newDates);
                  }} />
              </Grid>
            </Grid>

            {/* Vessel search */}
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142", mb: 1 }}>Select Vessel</Typography>
            <TextField
              fullWidth size="small" placeholder="Search by vessel name, registration, or owner…"
              value={vesselSearch}
              onChange={(e) => { setVesselSearch(e.target.value); if (selectedVessel) { setSelectedVessel(null); setAvailableSlips([]); setSelectedSlip(null); } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }}
              sx={{ ...fieldSx, mb: 1 }}
            />

            {/* Vessel results dropdown */}
            {vessels.length > 0 && !selectedVessel && (
              <Paper elevation={2} sx={{ borderRadius: "12px", border: "1px solid rgba(47,99,173,0.12)", overflow: "hidden", mb: 2, maxHeight: 220, overflowY: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Vessel", "LOA × Beam", "Power", "Owner", ""].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.72rem", backgroundColor: "#f8fafc", py: 0.75 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vessels.map((v) => (
                      <TableRow key={v.id} hover sx={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedVessel(v);
                          setVesselSearch(v.vessel_name);
                          setVessels([]);
                          searchSlipsForVessel(v, null);
                        }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem" }}>{v.vessel_name}</TableCell>
                        <TableCell sx={{ fontSize: "0.78rem" }}>{v.loa ? `${v.loa} × ${v.beam}` : "N/A"}</TableCell>
                        <TableCell sx={{ fontSize: "0.78rem" }}>{v.power_required || "N/A"}</TableCell>
                        <TableCell sx={{ fontSize: "0.78rem", color: "#6b778c" }}>{v.owner_display || "N/A"}</TableCell>
                        <TableCell><Button size="small" variant="outlined" sx={{ borderRadius: "8px", textTransform: "none", fontSize: "0.72rem" }}>Select</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}

            {/* Selected vessel chip. shows expiry warnings immediately */}
            {selectedVessel && (() => {
              const today = new Date(); today.setHours(0,0,0,0);
              const insExp = selectedVessel.insurance_expiry ? new Date(String(selectedVessel.insurance_expiry).slice(0,10) + "T00:00:00") : null;
              const regExp = selectedVessel.registration_expiry ? new Date(String(selectedVessel.registration_expiry).slice(0,10) + "T00:00:00") : null;
              const insExpired = insExp && insExp < today;
              const regExpired = regExp && regExp < today;
              const hasExpiry  = insExpired || regExpired;
              return (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.25, borderRadius: "10px",
                    backgroundColor: hasExpiry ? "rgba(220,38,38,0.05)" : "rgba(21,101,192,0.06)",
                    border: `1px solid ${hasExpiry ? "rgba(220,38,38,0.3)" : "rgba(21,101,192,0.2)"}` }}>
                    <DirectionsBoatIcon sx={{ fontSize: 18, color: hasExpiry ? "#dc2626" : "#1565c0" }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: hasExpiry ? "#dc2626" : "#1565c0" }}>{selectedVessel.vessel_name}</Typography>
                      <Typography variant="caption" sx={{ color: "#5f6b7a" }}>
                        {selectedVessel.owner_display}
                        {selectedVessel.loa ? ` · LOA ${selectedVessel.loa} ft` : ""}
                        {selectedVessel.beam ? ` · Beam ${selectedVessel.beam} ft` : ""}
                        {selectedVessel.draft ? ` · Draft ${selectedVessel.draft} ft` : ""}
                        {selectedVessel.power_required && selectedVessel.power_required !== "none" ? ` · ${selectedVessel.power_required}` : ""}
                      </Typography>
                    </Box>
                    <Button size="small" sx={{ color: "#6b778c", textTransform: "none", fontSize: "0.72rem" }}
                      onClick={() => { setSelectedVessel(null); setVesselSearch(""); setAvailableSlips([]); setSelectedSlip(null); setErr(""); }}>
                      Change
                    </Button>
                  </Box>
                  {insExpired && <Alert severity="error" sx={{ mt: 0.75, borderRadius: "8px", py: 0.25 }}>⚠ Insurance expired {insExp.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}. reservation will be blocked</Alert>}
                  {regExpired && <Alert severity="error" sx={{ mt: 0.5,  borderRadius: "8px", py: 0.25 }}>⚠ Registration expired {regExp.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}. reservation will be blocked</Alert>}
                </Box>
              );
            })()}

            {/* Available slips. appear automatically once vessel is selected */}
            {searching && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3, justifyContent: "center" }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ color: "#6b778c" }}>Finding available slips for {selectedVessel?.vessel_name}…</Typography>
              </Box>
            )}

            {!searching && availableSlips.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142", mb: 1 }}>
                  Available Slips
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: "#6b778c" }}>
                   . filtered to fit {selectedVessel?.vessel_name} · {availableSlips.length} slip{availableSlips.length !== 1 ? "s" : ""}
                  </Typography>
                </Typography>
                <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid rgba(47,99,173,0.1)", overflow: "hidden" }}>
                  <TableContainer sx={{ maxHeight: 280 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["Slip", "Dock", "Capacity L×B×D", "Amenities", "Daily Rate", ""].map((h) => (
                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.72rem", backgroundColor: "#f8fafc" }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availableSlips.map((s) => {
                          const isSelected = selectedSlip?.id === s.id;
                          return (
                            <TableRow key={s.id} hover sx={{ cursor: "pointer", backgroundColor: isSelected ? "rgba(21,101,192,0.08)" : undefined }}
                              onClick={() => { setSelectedSlip(isSelected ? null : s); setErr(""); }}>
                              <TableCell sx={{ fontWeight: 700 }}>
                                {isSelected && <CheckCircleIcon sx={{ fontSize: 14, color: "#1565c0", mr: 0.5, verticalAlign: "middle" }} />}
                                {s.slip_number}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.78rem", color: "#6b778c" }}>{s.dock_name}</TableCell>
                              <TableCell sx={{ fontSize: "0.78rem" }}>{s.max_length ? `${s.max_length}×${s.max_beam}×${s.max_draft}` : "N/A"}</TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                  {s.power_30amp && <Chip label="30A" size="small" sx={{ height: 18, fontSize: "0.62rem" }} />}
                                  {s.power_50amp && <Chip label="50A" size="small" sx={{ height: 18, fontSize: "0.62rem" }} />}
                                  {s.water && <Chip label="Water" size="small" sx={{ height: 18, fontSize: "0.62rem" }} />}
                                  {s.wifi  && <Chip label="WiFi"  size="small" sx={{ height: 18, fontSize: "0.62rem" }} />}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, color: "#2f63ad", fontSize: "0.82rem" }}>
                                {s.daily_rate ? `$${s.daily_rate}/day` : "N/A"}
                              </TableCell>
                              <TableCell>
                                <Button size="small" variant={isSelected ? "contained" : "outlined"}
                                  sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: "0.72rem",
                                    ...(isSelected ? { background: "#1565c0" } : {}) }}>
                                  {isSelected ? "Selected ✓" : "Select"}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>
            )}

            {!searching && selectedVessel && availableSlips.length === 0 && err && (
              <Alert severity="warning" sx={{ borderRadius: "10px" }}>
                <strong>No available slips for {selectedVessel.vessel_name}</strong>
                <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5, fontSize: "0.8rem" }}>
                  {selectedVessel.loa   && <li>LOA: {selectedVessel.loa} ft. slip must have max_length ≥ {selectedVessel.loa} ft</li>}
                  {selectedVessel.beam  && <li>Beam: {selectedVessel.beam} ft. slip must have max_beam ≥ {selectedVessel.beam} ft</li>}
                  {selectedVessel.draft && <li>Draft: {selectedVessel.draft} ft. slip must have max_draft ≥ {selectedVessel.draft} ft</li>}
                  {selectedVessel.power_required && selectedVessel.power_required !== "none" && <li>Power: {selectedVessel.power_required} required</li>}
                </Box>
                <Typography variant="caption" sx={{ color: "#6b778c" }}>Check the vessel dimensions or the marina slip configuration.</Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* ── STEP 1: Confirm ── */}
        {step === 1 && (
          <Box>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", backgroundColor: "rgba(47,99,173,0.04)", border: "1px solid rgba(47,99,173,0.1)", mb: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 1.5 }}>Booking Summary</Typography>
              {[
                ["Vessel", `${selectedVessel?.vessel_name} (${selectedVessel?.registration_number || "No reg #"})`],
                ["Owner",  selectedVessel?.owner_display],
                ["Slip",   `${selectedSlip?.slip_number}, ${selectedSlip?.dock_name}`],
                ["Dates",  `${fmtDate(searchForm.start_date)} → ${fmtDate(searchForm.end_date)} (${nights} night${nights !== 1 ? "s" : ""})`],
                ...(estimatedTotal ? [["Est. Total", `$${estimatedTotal} @ $${selectedSlip?.daily_rate}/night`]] : []),
              ].map(([label, value]) => (
                <Box key={label} sx={{ display: "flex", gap: 2, mb: 0.75 }}>
                  <Typography variant="body2" sx={{ width: 80, color: "#6b778c", flexShrink: 0 }}>{label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>{value}</Typography>
                </Box>
              ))}
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>Reservation Type</InputLabel>
                  <Select value={resForm.reservation_type} label="Reservation Type"
                    onChange={(e) => setResForm((p) => ({ ...p, reservation_type: e.target.value }))}>
                    {RES_TYPES.map((t) => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline rows={2} label="Notes" value={resForm.notes}
                  onChange={(e) => setResForm((p) => ({ ...p, notes: e.target.value }))} sx={fieldSx} />
              </Grid>
            </Grid>

            {dimensionError && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2, borderRadius: "10px" }}
                action={
                  <Button size="small" color="warning" variant="outlined"
                    sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
                    onClick={() => handleConfirm(true)} disabled={saving}>
                    Book Anyway
                  </Button>
                }>
                <strong>Dimension warning:</strong> {dimensionError}
              </Alert>
            )}

            {conflictError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: "10px" }}>
                <strong>Conflict:</strong> Slip is already booked {fmtDate(conflictError.start_date)} → {fmtDate(conflictError.end_date)} (status: {conflictError.status})
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.25 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>Cancel</Button>
        {step === 0 && (
          <Button variant="contained" sx={primaryBtnSx}
            disabled={!selectedVessel || !selectedSlip}
            onClick={() => { setStep(1); setErr(""); }}>
            Continue to Confirm
          </Button>
        )}
        {step === 1 && (
          <>
            <Button variant="outlined" sx={secondaryBtnSx} onClick={() => { setStep(0); setDimensionError(null); setConflictError(null); setErr(""); }} disabled={saving}>
              Back
            </Button>
            {!dimensionError && (
              <Button variant="contained" sx={primaryBtnSx} onClick={() => handleConfirm(false)} disabled={saving}>
                {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Confirm Reservation"}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

/* ── Cancel dialog ──────────────────────────────────────────────────────── */
const CancelDialog = ({ open, reservation, onClose, onCancelled }) => {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setReason(""); }, [open]);

  const handleCancel = async () => {
    setSaving(true);
    try {
      await api.post(`/slip-reservations/${reservation.id}/cancel`, { reason });
      onCancelled();
      onClose();
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ fontWeight: 700, pt: 3 }}>Cancel Reservation</DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        <Typography variant="body2" sx={{ color: "#445", mb: 2 }}>
          Cancel reservation for <strong>{reservation?.vessel_name}</strong> ({reservation?.slip_number})?
        </Typography>
        <TextField fullWidth multiline rows={3} label="Reason (optional)" value={reason}
          onChange={(e) => setReason(e.target.value)} sx={fieldSx} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>Keep Reservation</Button>
        <Button onClick={handleCancel} variant="contained" color="error"
          sx={{ ...secondaryBtnSx, fontWeight: 700 }} disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Cancel Reservation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Main component ─────────────────────────────────────────────────────── */
const DEFAULT_FILTERS = { search: "", status: "", type: "", from: plusDays(-7), to: plusDays(14) };

const MarinaReservations = ({ marinaId, showSnack }) => {
  const perms = useContext(PermissionsContext);
  const canManageRes = perms.includes("marina.manage_reservations");
  const canCheckin   = perms.includes("marina.checkin") || canManageRes;

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filters, setFilters]           = useState(DEFAULT_FILTERS);
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(25);
  const [newResOpen, setNewResOpen]     = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [editTarget, setEditTarget]     = useState(null);
  const [chargeTarget, setChargeTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const debounce = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      const res = await fetch(`${window.location.origin}/api/marina-reservations-export/${marinaId}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href=url; a.download=`marina-reservations-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error("Export failed:", err); }
    finally { setExporting(false); }
  };

  /* ── View state: list | calendar ── */
  const [viewMode, setViewMode] = useState("list");
  const [calView, setCalView]   = useState("month");
  const [calDate, setCalDate]   = useState(() => new Date());

  /* ── When in calendar mode sync filters to the visible period ── */
  useEffect(() => {
    if (viewMode !== "calendar") return;
    let from, to;
    if (calView === "day") {
      from = isoDate(calDate);
      to   = from;
    } else if (calView === "week") {
      const mon = startOfWeek(calDate);
      from = isoDate(mon);
      to   = isoDate(addDaysToDate(mon, 6));
    } else {
      from = isoDate(new Date(calDate.getFullYear(), calDate.getMonth(), 1));
      to   = isoDate(new Date(calDate.getFullYear(), calDate.getMonth() + 1, 0));
    }
    setFilters((p) => ({ ...p, from, to }));
  }, [viewMode, calView, calDate]);

  const fetchReservations = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ marina_id: marinaId });
      if (f.status) params.set("status", f.status);
      if (f.type)   params.set("reservation_type", f.type);
      if (f.from)   params.set("start_date", f.from);
      if (f.to)     params.set("end_date", f.to);
      const res = await api.get(`/slip-reservations?${params}`);
      const all = res.data || [];
      const search = f.search.toLowerCase();
      setReservations(search
        ? all.filter((r) => (r.vessel_name + r.owner_name + r.slip_number + r.dock_name).toLowerCase().includes(search))
        : all);
    } catch { showSnack("Failed to load reservations", "error"); }
    finally { setLoading(false); }
  }, [marinaId, showSnack]);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setPage(0); fetchReservations(filters); }, 300);
    return () => clearTimeout(debounce.current);
  }, [filters, fetchReservations]);

  const setFilter = (k) => (e) => setFilters((p) => ({ ...p, [k]: e.target.value }));
  const hasFilters = filters.status || filters.type || filters.search;

  const doAction = async (id, action) => {
    setActionLoading((p) => ({ ...p, [id]: action }));
    try {
      await api.post(`/slip-reservations/${id}/${action}`);
      showSnack(`${action === "check-in" ? "Checked in" : "Checked out"} successfully`);
      fetchReservations(filters);
    } catch (e) {
      showSnack(e.response?.data?.message || "Action failed", "error");
    } finally { setActionLoading((p) => ({ ...p, [id]: null })); }
  };

  const rowBg = (status) => ({
    checked_in:  "rgba(22,163,74,0.03)",
    confirmed:   "rgba(47,99,173,0.03)",
    cancelled:   "rgba(220,38,38,0.02)",
    checked_out: "rgba(0,0,0,0.01)",
  }[status] || "transparent");

  const paginated = reservations.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  /* ── View mode switch ── */
  const switchView = (mode) => {
    setViewMode(mode);
    if (mode === "list") {
      setFilters(DEFAULT_FILTERS);
    }
  };

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
            Reservations
            {!loading && (
              <Chip label={reservations.length} size="small"
                sx={{ ml: 1, height: 20, fontWeight: 700, backgroundColor: "rgba(47,99,173,0.1)", color: "#2f63ad" }} />
            )}
          </Typography>

          {/* List / Calendar toggle */}
          <Box sx={{ display: "flex", gap: "2px", backgroundColor: "#f1f5f9", borderRadius: "10px", p: "3px" }}>
            <Tooltip title="List view">
              <IconButton size="small" onClick={() => switchView("list")}
                sx={{
                  borderRadius: "8px", width: 32, height: 32,
                  backgroundColor: viewMode === "list" ? "#fff" : "transparent",
                  color: viewMode === "list" ? "#2f63ad" : "#6b778c",
                  boxShadow: viewMode === "list" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                }}>
                <ViewListIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Calendar view">
              <IconButton size="small" onClick={() => switchView("calendar")}
                sx={{
                  borderRadius: "8px", width: 32, height: 32,
                  backgroundColor: viewMode === "calendar" ? "#fff" : "transparent",
                  color: viewMode === "calendar" ? "#2f63ad" : "#6b778c",
                  boxShadow: viewMode === "calendar" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                }}>
                <CalendarMonthIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {canManageRes && (
          <Button variant="contained" startIcon={<AddIcon />} sx={primaryBtnSx} onClick={() => setNewResOpen(true)}>
            New Reservation
          </Button>
        )}
      </Box>

      {/* ── Filters (list view only) ── */}
      {viewMode === "list" && (
        <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: "14px", border: "1px solid rgba(47,99,173,0.08)", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", backgroundColor: "rgba(255,255,255,0.96)" }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
            <TextField size="small" placeholder="Search vessel, owner, slip…" value={filters.search} onChange={setFilter("search")}
              sx={{ ...fieldSx, minWidth: 220, flex: "1 1 220px" }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }} />

            <FormControl size="small" sx={{ ...fieldSx, minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filters.status} label="Status" onChange={setFilter("status")}>
                <MenuItem value="">All Statuses</MenuItem>
                {Object.entries(RES_STATUS_STYLES).map(([v, { label }]) => <MenuItem key={v} value={v}>{label}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ ...fieldSx, minWidth: 140 }}>
              <InputLabel>Type</InputLabel>
              <Select value={filters.type} label="Type" onChange={setFilter("type")}>
                <MenuItem value="">All Types</MenuItem>
                {RES_TYPES.map((t) => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField size="small" type="date" label="From" value={filters.from} onChange={setFilter("from")} InputLabelProps={{ shrink: true }} sx={{ ...fieldSx, minWidth: 150 }} />
            <TextField size="small" type="date" label="To"   value={filters.to}   onChange={setFilter("to")}   InputLabelProps={{ shrink: true }} sx={{ ...fieldSx, minWidth: 150 }} />

            {hasFilters && (
              <Tooltip title="Clear filters">
                <Button size="small" variant="outlined" startIcon={<FilterListOffIcon />}
                  onClick={() => setFilters(DEFAULT_FILTERS)}
                  sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, borderColor: "rgba(47,99,173,0.25)", color: "#2f63ad" }}>
                  Clear
                </Button>
              </Tooltip>
            )}
          </Box>
        </Paper>
      )}

      {/* ── Calendar view ── */}
      {viewMode === "calendar" && (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", border: "1px solid rgba(47,99,173,0.08)", boxShadow: "0 4px 14px rgba(0,0,0,0.04)" }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#2f63ad" }} />
            </Box>
          )}
          {!loading && (
            <CalendarView
              reservations={reservations}
              calView={calView}
              calDate={calDate}
              onNavigate={setCalDate}
              onViewChange={setCalView}
              onResClick={(r) => setEditTarget(r)}
            />
          )}
        </Paper>
      )}

      {/* ── List view ── */}
      {viewMode === "list" && (
        <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid rgba(47,99,173,0.08)", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  {["Dates", "Slip", "Vessel", "Owner", "Type", "Status", "Total", "Charges", ...(canManageRes || canCheckin ? ["Actions"] : [])].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a", py: 1.25, whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 9 }).map((__, j) => (
                        <TableCell key={j}><Box sx={{ height: 18, backgroundColor: "#f0f0f0", borderRadius: 1 }} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: "center", py: 6 }}>
                      <DirectionsBoatIcon sx={{ fontSize: 40, color: "#d0d7e0", mb: 1, display: "block", mx: "auto" }} />
                      <Typography variant="body2" sx={{ color: "#9aa5b4" }}>No reservations found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : paginated.map((r) => {
                  const isLoading = actionLoading[r.id];
                  return (
                    <TableRow key={r.id} hover sx={{ backgroundColor: rowBg(r.status), "&:last-child td": { border: 0 } }}>
                      <TableCell sx={{ fontSize: "0.8rem", color: "#445", whiteSpace: "nowrap" }}>
                        {fmtDate(r.start_date)}<br />
                        <Typography variant="caption" sx={{ color: "#9aa5b4" }}>→ {fmtDate(r.end_date)}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#223142", fontSize: "0.85rem" }}>{r.slip_number}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>{r.vessel_name}</Typography>
                        <Typography variant="caption" sx={{ color: "#9aa5b4", fontFamily: "monospace" }}>{r.registration_number || ""}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", color: "#445" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {r.owner_name || "N/A"}
                          {(r.vessel_id || r.owner_name) && (
                            <OwnerInfoPopover
                              ownerType={r.owner_type}
                              ownerId={r.owner_id}
                              memberCode={r.property_id}
                              vesselId={r.vessel_id}
                              displayName={r.owner_name}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", color: "#6b778c" }}>
                        {r.reservation_type?.charAt(0).toUpperCase() + r.reservation_type?.slice(1)}
                      </TableCell>
                      <TableCell><ResStatusChip status={r.status} /></TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#223142" }}>
                        {r.total_amount != null ? `$${Number(r.total_amount).toFixed(2)}` : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View / Add Charges">
                          <IconButton size="small" sx={{ color: "#2f63ad" }} onClick={() => setChargeTarget(r)}>
                            <ReceiptLongIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      {(canManageRes || canCheckin) && (
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {canManageRes && !["cancelled", "no_show"].includes(r.status) && (
                            <Tooltip title="Edit Reservation">
                              <IconButton size="small" sx={{ color: "#6b778c" }} disabled={!!isLoading} onClick={() => setEditTarget(r)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canCheckin && (r.status === "confirmed" || r.status === "pending") && (
                            <Tooltip title="Check In">
                              <IconButton size="small" sx={{ color: "#16a34a" }} disabled={!!isLoading} onClick={() => doAction(r.id, "check-in")}>
                                {isLoading === "check-in" ? <CircularProgress size={16} /> : <CheckCircleIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                          {canCheckin && r.status === "checked_in" && (
                            <Tooltip title="Check Out">
                              <IconButton size="small" sx={{ color: "#ea580c" }} disabled={!!isLoading} onClick={() => doAction(r.id, "check-out")}>
                                {isLoading === "check-out" ? <CircularProgress size={16} /> : <ExitToAppIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                          {canManageRes && !["cancelled", "checked_out", "no_show"].includes(r.status) && (
                            <Tooltip title="Cancel">
                              <IconButton size="small" color="error" disabled={!!isLoading} onClick={() => setCancelTarget(r)}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            component="div" count={reservations.length} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{ "& .MuiTablePagination-toolbar": { px: 2 } }}
          />
        </Paper>
      )}

      {/* ── Dialogs ── */}
      <EditReservationDialog
        open={Boolean(editTarget)}
        reservation={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { fetchReservations(filters); showSnack("Reservation updated"); }}
      />
      <ReservationChargesDialog
        open={Boolean(chargeTarget)}
        reservation={chargeTarget}
        marinaId={marinaId}
        onClose={() => setChargeTarget(null)}
        onUpdated={() => fetchReservations(filters)}
      />
      <NewReservationDialog
        open={newResOpen}
        marinaId={marinaId}
        onClose={() => setNewResOpen(false)}
        onCreated={() => { fetchReservations(filters); showSnack("Reservation confirmed!"); }}
      />
      <CancelDialog
        open={Boolean(cancelTarget)}
        reservation={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={() => { fetchReservations(filters); showSnack("Reservation cancelled"); }}
      />
    </Box>
  );
};

export default MarinaReservations;
